import type { OfferKind } from '@revelio/core/global-ai-assistant/offer-marker';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type { AssistantPostingsMode } from '../CustomDash/components/DataProvider/postings/postings-metric-mode';
import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';
import type {
  CustomDashAssistantEntityType,
  GlobalAssistantEntityType,
} from './global-ai-assistant-api';

export type GlobalAiTranscriptMessage =
  | { id: string; role: 'user'; text: string }
  | {
      id: string;
      role: 'assistant';
      kind: 'text';
      text: string;
      /**
       * Progress narration streamed by a tool mid-run (see
       * global-ai-tool-progress.ts). Rendered like any other narration, but
       * EXCLUDED from the conversation history sent to the model so transient
       * "building your dashboard…" chatter never pollutes the next turn's
       * context or the summarizer.
       */
      ephemeral?: boolean;
      /**
       * Message ids of the offer messages this narration's inline
       * `[phrase](offer:<kind>)` triggers act on, stamped by the client tool
       * handler that appended the narration (it created/knows the backing
       * message). The drawer resolves each trigger by LOOKING UP this id —
       * never by transcript position — so a marker whose target is absent
       * renders as plain text, and a chip renders iff its id is unstamped.
       * kinds: nav → nav_confirm id; build → create_page_confirm id;
       * page → single_chart id (answerAsText or rendered); custom, filters →
       * the rendered single_chart id the follow-up acts on;
       * geo_tax → the taxonomy id to switch to (read off the turn's
       * filter_response artifact, never off the model's text).
       */
      offerTargets?: Partial<Record<OfferKind, string>>;
      /**
       * Prompt that produced THIS narration. The `offer:geo_tax` trigger
       * re-resolves filters after switching taxonomy, and must re-resolve the
       * ask that earned the offer — not whichever prompt happens to be latest
       * when the user clicks. Same reason `single_chart` carries a prompt.
       */
      prompt?: string;
      /**
       * The `offer:geo_tax` switch came from a chart's level note, not the
       * ask's filters: the chart is a preview with its own filters, so the
       * click re-asks the question rather than applying it to the page.
       */
      geoTaxReask?: boolean;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'clarification';
      text: string;
      clarificationOptions?: GlobalAssistantEntityType[];
    }
  | { id: string; role: 'assistant'; kind: 'error'; text: string }
  /**
   * The user pressed stop while this turn was running. Carries no text — it
   * renders as a muted "Stopped" marker, and the turn's output is discarded
   * around it (see `discardStoppedTurnOutput`). Appended ONLY for an explicit
   * user stop, never for the drawer-close / hydrate aborts (see `cancelTurn`).
   */
  | { id: string; role: 'assistant'; kind: 'stopped' }
  | {
      id: string;
      role: 'assistant';
      kind: 'single_chart';
      intentSummary?: string;
      /** Factual line shown under the embedded chart (from intent \`chartSubtitle\`). */
      chartSubtitle?: string;
      item: PositionedItem;
      filters?: TalentDiscoveryAiFilterSearchResponseV2;
      entityType: CustomDashAssistantEntityType;
      /**
       * Prompt that produced this chart. Used when opening a chart as a
       * custom page so follow-up filter context stays tied to this chart,
       * not whichever prompt happens to be latest.
       */
      prompt?: string;
      pipelineRequestId?: string;
      standardPagePath?: string;
      /** How a postings chart is counted; absent = unweighted. */
      postingsMode?: AssistantPostingsMode;
      /**
       * Single-value answer: metadata-only message carrying the offer:page
       * wiring (standardPagePath + filters + prompt). The figure itself comes
       * from the headless digest service and is delivered as prose — this
       * message never mounts a chart (excluded from chart-preview liveness),
       * renders no card, and shows no "open as custom page" button.
       */
      answerAsText?: boolean;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'report_offers';
      offers: Array<{
        reportType: string;
        reportName: string;
        description?: string;
        sampleLink: string;
        confidence: number;
      }>;
      prompt?: string;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'nav_confirm';
      optionId: string;
      /** Display name of the destination (e.g. "Company Compensation"). */
      pageName: string;
      /** Continuation seed: the user's task so a click can resume it. */
      continuationPrompt?: string;
      /**
       * Set once the one-shot continuation resume has been consumed. Gates
       * ONLY the resume — navigation (+ filter re-apply) stays clickable
       * forever, including on rehydrated conversations.
       */
      resolved?: boolean;
      /**
       * Resolved search_filters artifact backing a DATA-GROUNDED offer
       * (`offer_page_or_custom` with `filterArtifactId` — the causal
       * ground-then-offer flow). When present, a click applies THIS exact
       * artifact on the destination (same deterministic text=link contract
       * as `single_chart.filters`), so "engineer attrition is 26%…" lands on
       * a page filtered to engineers — never the page's default slice, and
       * never an independent re-resolution of the prompt.
       */
      filters?: TalentDiscoveryAiFilterSearchResponseV2;
      /** Entity anchor for `filters` (mirrors single_chart.entityType). */
      entityType?: GlobalAssistantEntityType;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'create_page_confirm';
      prompt: string;
      entityType: CustomDashAssistantEntityType;
      /**
       * PRE-GENERATED layout (resolve-first Phase C): `create_custom_page` ran
       * the page-gen stream headlessly during the turn; the click seeds a new
       * draft with this exact layout and navigates — no empty draft, no
       * on-page build, no resumed turn. Absent → legacy confirm-navigate-
       * resume flow (also the automatic fallback when generation failed or
       * asked clarification questions).
       */
      generated?: {
        content: LLMTabContentForDispatch;
        filters?: TalentDiscoveryAiFilterSearchResponseV2;
      };
      /**
       * Resolved search_filters artifact backing a DATA-GROUNDED offer — the
       * SAME artifact `offer_page_or_custom` stored on its nav chip. Used as
       * the landing-filter FALLBACK when the lazy page-gen's own filter
       * resolution comes back empty/unresolved (e.g. its search could not
       * match the named company), so the built draft still lands scoped to
       * the entity the offer named instead of the page default.
       */
      filters?: TalentDiscoveryAiFilterSearchResponseV2;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'reasoning_trace';
      trace: import('./reasoning-trace').ReasoningTrace;
    };

export type GlobalAiInlineChartSpec = {
  messageId: string;
  item: PositionedItem;
  filters?: TalentDiscoveryAiFilterSearchResponseV2;
  entityType: CustomDashAssistantEntityType;
  chartSubtitle?: string;
};

export type GlobalAiSingleChartTranscriptMessage = Extract<
  GlobalAiTranscriptMessage,
  { kind: 'single_chart' }
>;

export function toGlobalAiInlineChartSpec(
  msg: GlobalAiSingleChartTranscriptMessage
): GlobalAiInlineChartSpec {
  return {
    messageId: msg.id,
    item: msg.item,
    filters: msg.filters,
    entityType: msg.entityType,
    chartSubtitle: msg.chartSubtitle,
  };
}

/**
 * Abort reason that marks a user-initiated stop, as opposed to the
 * drawer-close / hydrate / unmount aborts. The turn runner appends the
 * "Stopped" transcript marker only for THIS reason — appending it for every
 * abort would drop a marker into the transcript every time the drawer closes.
 *
 * Lives here rather than beside `cancelTurn` in the engine because the turn
 * runner also reads it, and the engine imports the runner — the other
 * direction would be a cycle.
 */
export const USER_STOP_ABORT_REASON = 'user_stop';

/** The muted "Stopped" marker appended when a user cancels a running turn. */
export function createStoppedMessage(): GlobalAiTranscriptMessage {
  return { id: crypto.randomUUID(), role: 'assistant', kind: 'stopped' };
}

/**
 * Transcript for a turn the user stopped: drop everything the assistant
 * produced after `outputBaseline` (the transcript length when the turn
 * started), keep the reasoning trace, and end with the "Stopped" marker.
 *
 * A half-finished turn leaves ACTIONABLE artifacts behind — a
 * `create_page_confirm` chip whose filters never arrived, a `report_offers`
 * card, a `nav_confirm`. Clicking one runs a build/navigation off state that
 * was never assembled, so a stop discards the turn's output wholesale rather
 * than trying to decide which half-formed pieces are safe. Narration text
 * goes too: its inline `[phrase](offer:kind)` triggers resolve by looking up
 * the backing message id, so orphaned text would still render offer prose
 * with nothing behind it.
 *
 * Indexed off a caller-supplied baseline rather than "everything after the
 * last user message" because silent seeded turns append no user message —
 * that rule would eat the PREVIOUS turn's output.
 */
export function discardStoppedTurnOutput(
  transcript: GlobalAiTranscriptMessage[],
  outputBaseline: number
): GlobalAiTranscriptMessage[] {
  const kept = transcript.filter(
    (message, index) =>
      index < outputBaseline ||
      (message.role === 'assistant' && message.kind === 'reasoning_trace')
  );
  return [...kept, createStoppedMessage()];
}
